import React, { useState, useEffect, useCallback } from "react";
import {
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";

import { CONSTANTS } from "../../../../library/common/constant";
import styles from "./Languages.module.scss";
import CustomDialog from "../../../../library/common/CustomDialog";
import SearchBar from "../../../../library/common/SearchBar/SearchBar";
import { api } from "../../../../services";
import MiscellaneousListing from "../../../../library/component/MiscellaneousListing/MiscellaneousListing";
import { throttle } from "lodash";

const {
  ALL_LANGUAGES_TITLE,
  ADD_NEW_LANGUAGE_BUTTON,
  SEARCH_LANGUAGES_LABEL,
  DELETE_CONFIRM_TITLE,
  DELETE_CONFIRM_MESSAGE,
  CANCEL_BUTTON,
  ADD_BUTTON,
  THROTTLE_DURATION,
} = CONSTANTS;

const Languages = () => {
  const [languages, setLanguages] = useState([]);
  const [filteredLanguages, setFilteredLanguages] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [languageToDelete, setLanguageToDelete] = useState(null);
  const [isModalOpen, setModalOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const [newLanguage, setNewLanguage] = useState({
    name: { en: "", hi: "" },
    code: "",
  });
  const [editingId, setEditingId] = useState(null);
  const [editedLanguage, setEditedLanguage] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const filterLanguages = useCallback(() => {
    if (languages?.length > 0) {
      setFilteredLanguages(
        languages?.filter(
          (item) =>
            item?.name?.en
              ?.toLowerCase()
              ?.includes(searchText?.toLowerCase()) ||
            item?.name?.hi
              ?.toLowerCase()
              ?.includes(searchText?.toLowerCase()) ||
            item?.code?.toLowerCase()?.includes(searchText?.toLowerCase())
        )
      );
    } else {
      setFilteredLanguages([]);
    }
  }, [searchText, languages]);

  const getLanguages = useCallback(async () => {
    try {
      const { data } = await api.get("/languages");
      setLanguages(data?.languages || []);
    } catch (error) {
      console.error("Failed to fetch languages", error);
    }
  }, []);

  useEffect(() => {
    getLanguages();
  }, [getLanguages]);

  useEffect(() => {
    filterLanguages();
  }, [filterLanguages]);

  const handleEditClicked = (id) => {
    const language = filteredLanguages?.find((item) => item?._id === id);
    setEditingId(id);
    setEditedLanguage(language);
  };

  const handleAddNewLanguage = () => {
    setModalOpen(true);
    setErrors({});
  };

  const handleOpenDeleteDialog = (language) => {
    setLanguageToDelete(language);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setLanguageToDelete(null);
    setOpenDeleteDialog(false);
  };

  const handleDeleteLanguage = throttle(async () => {
    try {
      setIsLoading(true);
      const _id = languageToDelete?._id;
      await api.delete(`/languages/` + _id);
      const updatedLanguages = languages?.filter(
        (language) => language?._id !== _id
      );
      setLanguages(updatedLanguages);
      filterLanguages();
      handleCloseDeleteDialog();
    } catch (error) {
      console.error("Failed to delete: ", error);
    } finally {
      setIsLoading(false);
    }
  }, THROTTLE_DURATION);

  const handleCloseModal = () => {
    setModalOpen(false);
    setNewLanguage({ name: { en: "", hi: "" }, code: "" });
    setErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "code") {
      setNewLanguage((prev) => ({
        ...prev,
        [name]: value,
      }));
    } else {
      setNewLanguage((prev) => ({
        ...prev,
        name: { ...prev.name, [name]: value },
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!newLanguage?.name?.en?.trim())
      newErrors.en = "English Name is required";
    if (!newLanguage?.name?.hi?.trim()) newErrors.hi = "Hindi Name is required";
    if (!newLanguage?.code?.trim()) newErrors.code = "Code is required";
    else if (newLanguage?.code?.trim().length !== 2)
      newErrors.code = "Code must have exactly 2 characters";
    return newErrors;
  };

  const handleAddLanguage = throttle(async () => {
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
    } else {
      try {
        setIsLoading(true);
        await api.post("/languages", newLanguage);
        setLanguages([...languages, newLanguage]);
        handleCloseModal();
      } catch (error) {
        console.error("Failed to post: ", error);
      } finally {
        setIsLoading(false);
      }
    }
  }, THROTTLE_DURATION);

  const handleSaveClicked = throttle(async () => {
    try {
      setIsLoading(true);
      await api.patch(`/languages/${editingId}`, {
        name: editedLanguage?.name,
        code: editedLanguage?.code,
      });
      const updatedLanguages = languages?.map((language) =>
        language?._id === editingId
          ? { ...language, ...editedLanguage }
          : language
      );
      setLanguages(updatedLanguages);
      setEditingId(null);
    } catch (error) {
      console.error("Failed to update: ", error);
    } finally {
      setIsLoading(false);
    }
  }, THROTTLE_DURATION);

  const handleEditedInputChange = (e) => {
    const { name, value } = e.target;
    setEditedLanguage((prev) => ({
      ...prev,
      name: { ...prev.name, [name]: value },
    }));
  };

  const handleSearch = (text) => {
    setSearchText(text);
  };

  return (
    <Paper className={styles.paper_container}>
      <Typography
        variant="h5"
        className={styles.header_title}
        textAlign={"center"}
      >
        {ALL_LANGUAGES_TITLE}
      </Typography>

      {/* Add new button and search bar */}
      <div className={styles.search_add_container}>
        <Button variant="contained" onClick={handleAddNewLanguage}>
          {ADD_NEW_LANGUAGE_BUTTON}
        </Button>
        <SearchBar
          placeholderText={SEARCH_LANGUAGES_LABEL}
          updateSearchText={handleSearch}
        />
      </div>

      {/* Table for displaying languages */}
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>English Name</TableCell>
              <TableCell>Hindi Name</TableCell>
              <TableCell>Code</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredLanguages.map((item) => (
              <MiscellaneousListing
                key={item?.code}
                isEditingItem={editingId === item?._id ? true : false}
                editedItem={editedLanguage}
                englishName={item?.name?.en}
                hindiName={item?.name?.hi}
                itemCode={item?.code}
                inputChange={handleEditedInputChange}
                saveClick={handleSaveClicked}
                editClick={() => handleEditClicked(item?._id)}
                deleteClick={() => handleOpenDeleteDialog(item)}
                disableDone={isLoading}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add new language dialog */}
      <Dialog open={isModalOpen} onClose={handleCloseModal}>
        <DialogTitle>{ADD_NEW_LANGUAGE_BUTTON}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="dense"
            label="English Name"
            name="en"
            value={newLanguage?.name?.en ?? ""}
            onChange={handleInputChange}
            error={!!errors?.en}
            helperText={errors?.en}
          />
          <TextField
            fullWidth
            margin="dense"
            label="Hindi Name"
            name="hi"
            value={newLanguage?.name?.hi ?? ""}
            onChange={handleInputChange}
            error={!!errors?.hi}
            helperText={errors?.hi}
          />
          <TextField
            fullWidth
            margin="dense"
            label="Code"
            name="code"
            value={newLanguage?.code ?? ""}
            onChange={handleInputChange}
            error={!!errors?.code}
            helperText={errors?.code}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>{CANCEL_BUTTON}</Button>
          <Button
            color="primary"
            variant="contained"
            onClick={handleAddLanguage}
            disabled={isLoading}
          >
            {ADD_BUTTON}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <CustomDialog
        open={openDeleteDialog}
        title={DELETE_CONFIRM_TITLE}
        content={DELETE_CONFIRM_MESSAGE}
        onClose={handleCloseDeleteDialog}
        onPrimaryClick={handleDeleteLanguage}
        onSecondaryClick={handleCloseDeleteDialog}
        disablePrimary={isLoading}
      />
    </Paper>
  );
};

export default Languages;
